import React, { useState, useEffect, useCallback } from 'react';
import { scrollToRegistration } from '../../utils/scrollUtils';
import './AutoGridFromDescription.css';

// ─── Types ────────────────────────────────────────────────────────────────

interface TrainingSession {
  id?: string;
  number: number;
  date?: string;
  startTime: string;
  endTime: string;
  grades: string;
  location?: TryoutLocation;
}

interface TrainingLocation {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface TrainingDetails {
  startDate: string;
  endDate: string;
  duration: string;
  gender: string;
  days: string[];
  location: TrainingLocation;
  trainingSessions: TrainingSession[];
  notes: string[];
  dropOffTime: string;
  pickUpTime: string;
  hasLimitedSpots: boolean;
  contactEmail: string;
  ageGroups: string[];
  maxParticipants: number | null;
}

interface TryoutSession {
  id?: string;
  number: number;
  date?: string;
  startTime: string;
  endTime: string;
  grades: string;
  location?: TryoutLocation;
}

interface TryoutLocation {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface TryoutDetails {
  startDate: string;
  endDate: string;
  duration: string;
  gender: string;
  days: string[];
  locations?: TryoutLocation[];
  location?: TryoutLocation;
  tryoutSessions: TryoutSession[];
  notes: string[];
  dropOffTime: string;
  pickUpTime: string;
  hasLimitedSpots: boolean;
  contactEmail: string;
  ageGroups: string[];
  maxParticipants: number | null;
  whatToBring: string[];
}

interface PricingPackage {
  id?: string;
  name: string;
  price: number;
  description: string;
}

interface RegistrationFormConfig {
  _id?: any;
  eventId?: string;
  season?: string;
  year?: number;
  isActive: boolean;
  requiresPayment: boolean;
  requiresQualification: boolean;
  pricing: {
    basePrice: number;
    packages: PricingPackage[];
  };
  description?: string;
  trainingDetails?: TrainingDetails;
  tryoutDetails?: TryoutDetails;
  tryoutName?: string;
  tryoutYear?: number;
  tryoutFee?: number;
  displayName?: string;
}

interface AutoGridFromDescriptionProps {
  config: RegistrationFormConfig;
  onRegister?: () => void;
  isLightTheme?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

const formatTimeRange = (startTime: string, endTime: string): string => {
  const formatTime = (time: string) => {
    const match = time.match(/(\d+)(?::(\d+))?\s*(am|pm)/i);
    if (match) {
      let hour = parseInt(match[1]);
      const minute = match[2] || '00';
      const period = match[3].toUpperCase();
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      return `${hour}:${minute.padStart(2, '0')} ${period}`;
    }
    return time;
  };
  return `${formatTime(startTime)} – ${formatTime(endTime)}`;
};

const formatDateRange = (startDate: string, endDate: string): string => {
  if (startDate && endDate) {
    return `${startDate} – ${endDate}`;
  }
  return startDate || endDate || '';
};

const formatDays = (days: string[]): string => {
  const dayAbbr: { [key: string]: string } = {
    Monday: 'Monday',
    Tuesday: 'Tuesday',
    Wednesday: 'Wednesday',
    Thursday: 'Thursday',
    Friday: 'Friday',
    Saturday: 'Saturday',
    Sunday: 'Sunday',
  };
  return days.map((day) => dayAbbr[day] || day.slice(0, 3)).join(' · ');
};

const getFullAddress = (location: any): string => {
  if (!location) return '';
  const parts = [
    location.address,
    location.city,
    location.state,
    location.zipCode,
  ].filter(Boolean);
  return parts.join(', ');
};

const hasValidDescription = (description?: string): boolean => {
  if (!description) return false;
  const strippedText = description.replace(/<[^>]*>/g, '').trim();
  return strippedText.length > 0;
};

// ─── Subcomponents ────────────────────────────────────────────────────────

const TileHead: React.FC<{
  icon: string;
  label: string;
  isLight?: boolean;
}> = ({ icon, label, isLight = false }) => (
  <div className='agd-head'>
    <i
      className={`ti ${icon}`}
      style={{ color: isLight ? '#594230' : '#ffffff', fontSize: '0.95rem' }}
    />
    <span
      style={{ color: isLight ? '#4a4a5a' : '#ffffff', fontSize: '0.95rem' }}
    >
      {label}
    </span>
  </div>
);

const InfoRow: React.FC<{
  icon: string;
  children: React.ReactNode;
  isLight?: boolean;
}> = ({ icon, children, isLight = false }) => (
  <li className='agd-row'>
    <i
      className={`ti ${icon}`}
      style={{
        color: isLight ? '#594230' : 'rgba(255,140,0,.7)',
        flexShrink: 0,
        marginTop: 2,
      }}
    />
    <span>{children}</span>
  </li>
);

// ─── Main Component ──────────────────────────────────────────────────────

const AutoGridFromDescription: React.FC<AutoGridFromDescriptionProps> = ({
  config,
  onRegister,
  isLightTheme = false,
}) => {
  const trainingDetails = config?.trainingDetails;
  const tryoutDetails = config?.tryoutDetails;
  const isTryout = !!tryoutDetails;

  // Button style override to prevent blinking
  const noBlinkStyle = {
    animation: 'none !important' as any,
    transition: 'none !important' as any,
    opacity: 1,
  };

  const getTryoutLocations = (): TryoutLocation[] => {
    if (!tryoutDetails) return [];
    if (tryoutDetails.locations && tryoutDetails.locations.length > 0) {
      return tryoutDetails.locations;
    }
    if (tryoutDetails.location && tryoutDetails.location.name) {
      return [tryoutDetails.location];
    }
    return [];
  };

  const isSimpleSession = (
    session: TrainingSession | TryoutSession,
  ): boolean => {
    return (
      !!session.date &&
      !!session.location?.name &&
      !session.startTime &&
      !session.endTime &&
      !session.grades
    );
  };

  const hasSessionData = (
    session: TrainingSession | TryoutSession,
  ): boolean => {
    return !!(
      session.date ||
      session.startTime ||
      session.endTime ||
      session.grades ||
      session.location?.name
    );
  };

  const getValidSessions = (
    sessions: (TrainingSession | TryoutSession)[],
  ): (TrainingSession | TryoutSession)[] => {
    return sessions.filter(hasSessionData);
  };

  const SimpleSessionItem: React.FC<{
    session: TrainingSession | TryoutSession;
    isLight?: boolean;
  }> = ({ session, isLight = false }) => (
    <div
      className={`agd-session-simple ${isLight ? 'agd-session-simple--light' : ''}`}
    >
      <div className='agd-session-simple-content'>
        <span className='agd-session-simple-date'>
          <i className='ti ti-calendar' />
          {session.date}
        </span>
        <span className='agd-session-simple-separator'>→</span>
        <span className='agd-session-simple-location'>
          <i className='ti ti-map-pin' />
          {session.location?.name}
        </span>
      </div>
    </div>
  );

  const DetailedSessionItem: React.FC<{
    session: TrainingSession | TryoutSession;
    isLight?: boolean;
  }> = ({ session, isLight = false }) => (
    <div
      className={`agd-session-card ${isLight ? 'agd-session-card--light' : ''}`}
    >
      <div className='agd-session-header'>
        <div className='agd-session-time'>
          {session.date && (
            <span className='agd-session-date'>
              <i className='ti ti-calendar' />
              {session.date}
            </span>
          )}
          {(session.startTime || session.endTime) && (
            <span className='agd-session-time-range'>
              <i className='ti ti-clock' />
              {session.startTime && session.endTime
                ? formatTimeRange(session.startTime, session.endTime)
                : session.startTime || session.endTime}
            </span>
          )}
        </div>
        {session.grades && (
          <span className='agd-session-grades'>
            <i className='ti ti-users' />
            Grades {session.grades}
          </span>
        )}
      </div>

      {session.location?.name && (
        <div className='agd-session-location'>
          <i className='ti ti-map-pin agd-session-location-icon' />
          <div className='agd-session-location-details'>
            <span className='agd-session-location-name'>
              {session.location.name}
            </span>
            {getFullAddress(session.location) && (
              <span className='agd-session-location-address'>
                {getFullAddress(session.location)}
              </span>
            )}
            {getFullAddress(session.location) && (
              <a
                className={`agd-session-map-link ${isLight ? 'agd-session-map-link--light' : ''}`}
                href={`https://www.google.com/maps/search/${encodeURIComponent([session.location.name, getFullAddress(session.location)].filter(Boolean).join(' '))}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <i className='ti ti-external-link' /> Open in Google Maps
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const FAQSection: React.FC = () => (
    <div
      className={`agd-tile agd-tile--faq ${isLightTheme ? 'agd-tile--faq-light' : ''}`}
    >
      <TileHead
        icon='ti-help-circle'
        label='Frequently Asked Questions'
        isLight={isLightTheme}
      />
      <div className='agd-faq-content'>
        <p
          className={`agd-faq-text ${isLightTheme ? 'agd-faq-text--light' : ''}`}
        >
          Have questions about our programs, schedules, or registration process?
        </p>
        <a
          href='/faq'
          className={`agd-faq-link ${isLightTheme ? 'agd-faq-link--light' : ''}`}
          style={{
            color: '#ffffff',
            background: isLightTheme
              ? 'rgba(89, 66, 48, 0.9)'
              : 'rgba(89, 66, 48, 0.8)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'none',
          }}
        >
          <i className='ti ti-message-question' style={{ color: '#ffffff' }} />
          <span style={{ color: '#ffffff' }}>
            Visit our FAQ page for answers to common questions
          </span>
          <i className='ti ti-arrow-right' style={{ color: '#ffffff' }} />
        </a>
      </div>
    </div>
  );

  const sortAgeGroups = (groups: string[]): string[] => {
    return [...groups].sort((a, b) => {
      const aIsCollege = a.toLowerCase().includes('college');
      const bIsCollege = b.toLowerCase().includes('college');
      if (aIsCollege && !bIsCollege) return 1;
      if (!aIsCollege && bIsCollege) return -1;
      return a.localeCompare(b);
    });
  };

  // ─── Training View ─────────────────────────────────────────────────────

  if (!isTryout && trainingDetails) {
    const hasValidTrainingDetails =
      trainingDetails.startDate ||
      trainingDetails.location?.name ||
      (trainingDetails.trainingSessions?.length || 0) > 0;

    if (!hasValidTrainingDetails) {
      const accent = isLightTheme ? '#594230' : '#594230';
      return (
        <div
          className={`agd-root ${isLightTheme ? 'agd-root--light' : 'agd-root--dark'}`}
        >
          <div className='agd-event'>
            <div
              className='agd-tile agd-tile--hdr'
              style={{ borderTop: `3px solid ${accent}` }}
            >
              <div
                className='agd-hdr-icon'
                style={{
                  color: accent,
                  background: `${accent}18`,
                  borderColor: `${accent}44`,
                }}
              >
                <i className='ti ti-ball-basketball' />
              </div>
              <h2 className='agd-title'>
                {config?.season} {config?.year} Training
              </h2>
            </div>
            {hasValidDescription(config?.description) && (
              <div className='agd-tile'>
                <div
                  dangerouslySetInnerHTML={{ __html: config.description! }}
                  style={{ lineHeight: 1.6 }}
                />
              </div>
            )}
            <div className='agd-tile agd-tile--cta'>
              <button
                className='agd-cta'
                style={{
                  background: accent,
                  boxShadow: `0 6px 20px ${accent}44`,
                  ...noBlinkStyle,
                }}
                onClick={() => onRegister?.()}
              >
                <i className='ti ti-user-plus' /> Register Now{' '}
                <i className='ti ti-arrow-right' />
              </button>
            </div>
            <FAQSection />
            <div className='agd-tile agd-tile--cta-bottom'>
              <button
                className='agd-cta-bottom'
                onClick={() => onRegister?.()}
                style={noBlinkStyle}
              >
                <i className='ti ti-user-plus' /> Register Now{' '}
                <i className='ti ti-arrow-right' />
              </button>
            </div>
          </div>
        </div>
      );
    }

    const accent = isLightTheme ? '#594230' : '#594230';
    const ageGroupsDisplay = trainingDetails.ageGroups?.length
      ? sortAgeGroups(trainingDetails.ageGroups).join(', ')
      : '';
    const handleRegister = () => onRegister?.();

    const validSessions = getValidSessions(
      trainingDetails.trainingSessions || [],
    );
    const simpleSessions = validSessions.filter(isSimpleSession);
    const detailedSessions = validSessions.filter(
      (session) => !isSimpleSession(session),
    );

    const hasPricingPackages =
      config.pricing?.packages && config.pricing.packages.length > 0;
    const hasBasePrice =
      typeof config.pricing?.basePrice === 'number' &&
      config.pricing.basePrice > 0;

    return (
      <div
        className={`agd-root ${isLightTheme ? 'agd-root--light' : 'agd-root--dark'}`}
      >
        <div className='agd-event'>
          <div
            className='agd-tile agd-tile--hdr'
            style={{ borderTop: `3px solid ${accent}` }}
          >
            <div
              className='agd-hdr-icon'
              style={{
                color: accent,
                background: `${accent}18`,
                borderColor: `${accent}44`,
              }}
            >
              <i className='ti ti-ball-basketball' />
            </div>
            <h2 className='agd-title'>
              {config?.season} {config?.year}
            </h2>
            {(trainingDetails.startDate || trainingDetails.endDate) && (
              <p className='agd-sub'>
                <i className='ti ti-calendar' style={{ opacity: 0.5 }} />{' '}
                {formatDateRange(
                  trainingDetails.startDate,
                  trainingDetails.endDate,
                )}
              </p>
            )}
            {trainingDetails.hasLimitedSpots && (
              <span
                className='agd-badge'
                style={{
                  color: accent,
                  background: `${accent}20`,
                  borderColor: `${accent}55`,
                }}
              >
                Limited Spots
              </span>
            )}
          </div>

          <div className='agd-tile agd-tile--cta'>
            <button
              className='agd-cta'
              style={{
                background: accent,
                boxShadow: `0 6px 20px ${accent}44`,
                ...noBlinkStyle,
              }}
              onClick={handleRegister}
            >
              <i className='ti ti-user-plus' /> Register Now{' '}
              <i className='ti ti-arrow-right' />
            </button>
          </div>

          {hasValidDescription(config?.description) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-article'
                label='About the Program'
                isLight={isLightTheme}
              />
              <div
                className={`agd-desc ${isLightTheme ? 'agd-desc--light' : ''}`}
                dangerouslySetInnerHTML={{ __html: config.description! }}
              />
            </div>
          )}

          {(ageGroupsDisplay ||
            trainingDetails.gender ||
            trainingDetails.duration ||
            (trainingDetails.days?.length || 0) > 0 ||
            trainingDetails.dropOffTime ||
            trainingDetails.pickUpTime ||
            trainingDetails.maxParticipants) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-info-circle'
                label='Program Details'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                {ageGroupsDisplay && (
                  <InfoRow icon='ti-school' isLight={isLightTheme}>
                    <strong>Ages / Grades:</strong> {ageGroupsDisplay}
                  </InfoRow>
                )}
                {trainingDetails.gender && (
                  <InfoRow icon='ti-gender-bigender' isLight={isLightTheme}>
                    <strong>Gender:</strong> {trainingDetails.gender}
                  </InfoRow>
                )}
                {trainingDetails.duration && (
                  <InfoRow icon='ti-clock-hour-4' isLight={isLightTheme}>
                    <strong>Duration:</strong> {trainingDetails.duration}
                  </InfoRow>
                )}
                {(trainingDetails.days?.length || 0) > 0 && (
                  <InfoRow icon='ti-calendar-week' isLight={isLightTheme}>
                    <strong>Days:</strong> {formatDays(trainingDetails.days)}
                  </InfoRow>
                )}
                {trainingDetails.dropOffTime && (
                  <InfoRow icon='ti-car' isLight={isLightTheme}>
                    <strong>Drop-off:</strong> {trainingDetails.dropOffTime}
                  </InfoRow>
                )}
                {trainingDetails.pickUpTime && (
                  <InfoRow icon='ti-car' isLight={isLightTheme}>
                    <strong>Pick-up:</strong> {trainingDetails.pickUpTime}
                  </InfoRow>
                )}
                {trainingDetails.maxParticipants && (
                  <InfoRow icon='ti-users' isLight={isLightTheme}>
                    <strong>Max Participants:</strong>{' '}
                    {trainingDetails.maxParticipants}
                  </InfoRow>
                )}
              </ul>
            </div>
          )}

          {(hasBasePrice || hasPricingPackages) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-currency-dollar'
                label='Pricing'
                isLight={isLightTheme}
              />

              {hasBasePrice && (
                <div
                  className={`agd-base-price ${isLightTheme ? 'agd-base-price--light' : ''}`}
                >
                  <span className='agd-base-price-amount'>
                    ${config.pricing.basePrice}
                  </span>
                  <span className='agd-base-price-label'>per child</span>
                </div>
              )}

              {hasPricingPackages && (
                <div
                  className='agd-packages-grid'
                  data-count={config.pricing.packages.length}
                >
                  {config.pricing.packages.map((pkg, idx) => (
                    <div
                      key={pkg.id || idx}
                      className={`agd-package-card ${isLightTheme ? 'agd-package-card--light' : ''}`}
                    >
                      <div className='agd-package-name'>{pkg.name}</div>
                      <div className='agd-package-price'>${pkg.price}</div>
                      {pkg.description && (
                        <div className='agd-package-description'>
                          {pkg.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(trainingDetails.location?.name ||
            getFullAddress(trainingDetails.location)) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-map-pin'
                label='Location'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                <InfoRow icon='ti-location-pin' isLight={isLightTheme}>
                  {trainingDetails.location?.name && (
                    <strong>{trainingDetails.location.name}</strong>
                  )}
                  {trainingDetails.location?.name &&
                    getFullAddress(trainingDetails.location) && <br />}
                  {getFullAddress(trainingDetails.location)}
                </InfoRow>
              </ul>
              {getFullAddress(trainingDetails.location) && (
                <a
                  className={`agd-map-link ${isLightTheme ? 'agd-map-link--light' : ''}`}
                  href={`https://www.google.com/maps/search/${encodeURIComponent([trainingDetails.location.name, getFullAddress(trainingDetails.location)].filter(Boolean).join(' '))}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <i className='ti ti-external-link' /> Open in Google Maps
                </a>
              )}
            </div>
          )}

          {validSessions.length > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-calendar-event'
                label='Training Schedule'
                isLight={isLightTheme}
              />
              <div className='agd-sessions-container'>
                {simpleSessions.length > 0 && (
                  <div className='agd-sessions-simple-list'>
                    {simpleSessions.map((session) => (
                      <SimpleSessionItem
                        key={session.id}
                        session={session}
                        isLight={isLightTheme}
                      />
                    ))}
                  </div>
                )}
                {detailedSessions.length > 0 && (
                  <div className='agd-sessions-detailed-list'>
                    {detailedSessions.map((session) => (
                      <DetailedSessionItem
                        key={session.id}
                        session={session}
                        isLight={isLightTheme}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {(trainingDetails.notes?.length || 0) > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-notes'
                label='Important Notes'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                {trainingDetails.notes.map((note, index) => (
                  <InfoRow
                    key={index}
                    icon='ti-info-square'
                    isLight={isLightTheme}
                  >
                    {note}
                  </InfoRow>
                ))}
              </ul>
            </div>
          )}

          {trainingDetails.contactEmail && (
            <div className='agd-tile'>
              <TileHead icon='ti-mail' label='Contact' isLight={isLightTheme} />
              <ul className='agd-list'>
                <InfoRow icon='ti-at' isLight={isLightTheme}>
                  <a
                    href={`mailto:${trainingDetails.contactEmail}`}
                    style={{ color: accent, textDecoration: 'none' }}
                  >
                    {trainingDetails.contactEmail}
                  </a>
                </InfoRow>
              </ul>
            </div>
          )}

          <FAQSection />

          <div className='agd-tile agd-tile--cta-bottom'>
            <button
              className='agd-cta-bottom'
              onClick={handleRegister}
              style={noBlinkStyle}
            >
              <i className='ti ti-user-plus' /> Register Now{' '}
              <i className='ti ti-arrow-right' />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Tryout View ──────────────────────────────────────────────────────

  if (isTryout && tryoutDetails) {
    const tryoutLocations = getTryoutLocations();
    const hasValidTryoutDetails =
      tryoutDetails.startDate ||
      tryoutLocations.length > 0 ||
      (tryoutDetails.tryoutSessions?.length || 0) > 0;

    if (!hasValidTryoutDetails) {
      const accent = isLightTheme ? '#f59e0b' : '#f59e0b';
      return (
        <div
          className={`agd-root ${isLightTheme ? 'agd-root--light' : 'agd-root--dark'}`}
        >
          <div className='agd-event'>
            <div
              className='agd-tile agd-tile--hdr'
              style={{ borderTop: `3px solid ${accent}` }}
            >
              <div
                className='agd-hdr-icon'
                style={{
                  color: accent,
                  background: `${accent}18`,
                  borderColor: `${accent}44`,
                }}
              >
                <i className='ti ti-target-arrow' />
              </div>
              <h2 className='agd-title'>
                {config.displayName || config.tryoutName || 'Tryout'}{' '}
                {config.tryoutYear || ''}
              </h2>
            </div>
            {hasValidDescription(config?.description) && (
              <div className='agd-tile'>
                <div
                  dangerouslySetInnerHTML={{ __html: config.description! }}
                  style={{ lineHeight: 1.6 }}
                />
              </div>
            )}
            <div className='agd-tile agd-tile--cta'>
              <button
                className='agd-cta'
                style={{
                  background: accent,
                  boxShadow: `0 6px 20px ${accent}44`,
                  ...noBlinkStyle,
                }}
                onClick={() => onRegister?.()}
              >
                <i className='ti ti-user-plus' /> Register for Tryout{' '}
                <i className='ti ti-arrow-right' />
              </button>
            </div>
            <FAQSection />
            <div className='agd-tile agd-tile--cta-bottom'>
              <button
                className='agd-cta-bottom'
                onClick={() => onRegister?.()}
                style={noBlinkStyle}
              >
                <i className='ti ti-user-plus' /> Register for Tryout{' '}
                <i className='ti ti-arrow-right' />
              </button>
            </div>
          </div>
        </div>
      );
    }

    const accent = isLightTheme ? '#f59e0b' : '#f59e0b';
    const ageGroupsDisplay = tryoutDetails.ageGroups?.length
      ? sortAgeGroups(tryoutDetails.ageGroups).join(', ')
      : '';
    const handleRegister = () => {
      onRegister?.();
      setTimeout(scrollToRegistration, 100);
    };

    const validTryoutSessions = getValidSessions(
      tryoutDetails.tryoutSessions || [],
    );
    const simpleTryoutSessions = validTryoutSessions.filter(isSimpleSession);
    const detailedTryoutSessions = validTryoutSessions.filter(
      (session) => !isSimpleSession(session),
    );

    return (
      <div
        className={`agd-root ${isLightTheme ? 'agd-root--light' : 'agd-root--dark'}`}
      >
        <div className='agd-event'>
          <div
            className='agd-tile agd-tile--hdr'
            style={{ borderTop: `3px solid ${accent}` }}
          >
            <div
              className='agd-hdr-icon'
              style={{
                color: accent,
                background: `${accent}18`,
                borderColor: `${accent}44`,
              }}
            >
              <i className='ti ti-target-arrow' />
            </div>
            <h2 className='agd-title'>
              {config.displayName || config.tryoutName || 'Tryout'}{' '}
              {config.tryoutYear || ''}
            </h2>
            {(tryoutDetails.startDate || tryoutDetails.endDate) && (
              <p className='agd-sub'>
                <i className='ti ti-calendar' style={{ opacity: 0.5 }} />{' '}
                {formatDateRange(
                  tryoutDetails.startDate,
                  tryoutDetails.endDate,
                )}
              </p>
            )}
            {tryoutDetails.hasLimitedSpots && (
              <span
                className='agd-badge'
                style={{
                  color: accent,
                  background: `${accent}20`,
                  borderColor: `${accent}55`,
                }}
              >
                Limited Spots
              </span>
            )}
          </div>

          <div className='agd-tile agd-tile--cta'>
            <button
              className='agd-cta'
              style={{
                background: accent,
                boxShadow: `0 6px 20px ${accent}44`,
                ...noBlinkStyle,
              }}
              onClick={handleRegister}
            >
              <i className='ti ti-user-plus' /> Register for Tryout{' '}
              <i className='ti ti-arrow-right' />
            </button>
          </div>

          {hasValidDescription(config?.description) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-article'
                label='About Tryouts'
                isLight={isLightTheme}
              />
              <div
                className={`agd-desc ${isLightTheme ? 'agd-desc--light' : ''}`}
                dangerouslySetInnerHTML={{ __html: config.description! }}
              />
            </div>
          )}

          {(ageGroupsDisplay ||
            tryoutDetails.gender ||
            tryoutDetails.duration ||
            (tryoutDetails.days?.length || 0) > 0 ||
            tryoutDetails.dropOffTime ||
            tryoutDetails.pickUpTime ||
            tryoutDetails.maxParticipants) && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-info-circle'
                label='Tryout Details'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                {ageGroupsDisplay && (
                  <InfoRow icon='ti-school' isLight={isLightTheme}>
                    <strong>Age Groups:</strong> {ageGroupsDisplay}
                  </InfoRow>
                )}
                {tryoutDetails.gender && (
                  <InfoRow icon='ti-gender-bigender' isLight={isLightTheme}>
                    <strong>Gender:</strong> {tryoutDetails.gender}
                  </InfoRow>
                )}
                {tryoutDetails.duration && (
                  <InfoRow icon='ti-clock-hour-4' isLight={isLightTheme}>
                    <strong>Duration:</strong> {tryoutDetails.duration}
                  </InfoRow>
                )}
                {(tryoutDetails.days?.length || 0) > 0 && (
                  <InfoRow icon='ti-calendar-week' isLight={isLightTheme}>
                    <strong>Days:</strong> {formatDays(tryoutDetails.days)}
                  </InfoRow>
                )}
                {tryoutDetails.dropOffTime && (
                  <InfoRow icon='ti-car' isLight={isLightTheme}>
                    <strong>Check-in:</strong> {tryoutDetails.dropOffTime}
                  </InfoRow>
                )}
                {tryoutDetails.pickUpTime && (
                  <InfoRow icon='ti-car' isLight={isLightTheme}>
                    <strong>Pick-up:</strong> {tryoutDetails.pickUpTime}
                  </InfoRow>
                )}
                {tryoutDetails.maxParticipants && (
                  <InfoRow icon='ti-users' isLight={isLightTheme}>
                    <strong>Max Participants:</strong>{' '}
                    {tryoutDetails.maxParticipants}
                  </InfoRow>
                )}
              </ul>
            </div>
          )}

          {typeof config.tryoutFee === 'number' && config.tryoutFee > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-currency-dollar'
                label='Price'
                isLight={isLightTheme}
              />
              <div
                className={`agd-base-price ${isLightTheme ? 'agd-base-price--light' : ''}`}
              >
                <span className='agd-base-price-amount'>
                  ${config.tryoutFee}
                </span>
                <span className='agd-base-price-label'>per player</span>
              </div>
            </div>
          )}

          {tryoutLocations.length > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-map-pin'
                label={tryoutLocations.length === 1 ? 'Location' : 'Locations'}
                isLight={isLightTheme}
              />
              <div className='agd-locations'>
                {tryoutLocations.map((location, idx) => (
                  <div key={idx} className='agd-location-item'>
                    <ul className='agd-list'>
                      <InfoRow icon='ti-location-pin' isLight={isLightTheme}>
                        <strong>{location.name}</strong>
                        {location.name && getFullAddress(location) && <br />}
                        {getFullAddress(location)}
                      </InfoRow>
                    </ul>
                    {getFullAddress(location) && (
                      <a
                        className={`agd-map-link ${isLightTheme ? 'agd-map-link--light' : ''}`}
                        href={`https://www.google.com/maps/search/${encodeURIComponent([location.name, getFullAddress(location)].filter(Boolean).join(' '))}`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <i className='ti ti-external-link' /> Open in Google
                        Maps
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {validTryoutSessions.length > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-calendar-event'
                label='Tryout Schedule'
                isLight={isLightTheme}
              />
              <div className='agd-sessions-container'>
                {simpleTryoutSessions.length > 0 && (
                  <div className='agd-sessions-simple-list'>
                    {simpleTryoutSessions.map((session) => (
                      <SimpleSessionItem
                        key={session.id}
                        session={session}
                        isLight={isLightTheme}
                      />
                    ))}
                  </div>
                )}
                {detailedTryoutSessions.length > 0 && (
                  <div className='agd-sessions-detailed-list'>
                    {detailedTryoutSessions.map((session) => (
                      <DetailedSessionItem
                        key={session.id}
                        session={session}
                        isLight={isLightTheme}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {(tryoutDetails.whatToBring?.length || 0) > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-backpack'
                label='What to Bring'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                {tryoutDetails.whatToBring.map((item, idx) => (
                  <InfoRow key={idx} icon='ti-check' isLight={isLightTheme}>
                    {item}
                  </InfoRow>
                ))}
              </ul>
            </div>
          )}

          {(tryoutDetails.notes?.length || 0) > 0 && (
            <div className='agd-tile'>
              <TileHead
                icon='ti-notes'
                label='Important Notes'
                isLight={isLightTheme}
              />
              <ul className='agd-list'>
                {tryoutDetails.notes.map((note, idx) => (
                  <InfoRow
                    key={idx}
                    icon='ti-info-square'
                    isLight={isLightTheme}
                  >
                    {note}
                  </InfoRow>
                ))}
              </ul>
            </div>
          )}

          {tryoutDetails.contactEmail && (
            <div className='agd-tile'>
              <TileHead icon='ti-mail' label='Contact' isLight={isLightTheme} />
              <ul className='agd-list'>
                <InfoRow icon='ti-at' isLight={isLightTheme}>
                  <a
                    href={`mailto:${tryoutDetails.contactEmail}`}
                    style={{ color: accent, textDecoration: 'none' }}
                  >
                    {tryoutDetails.contactEmail}
                  </a>
                </InfoRow>
              </ul>
            </div>
          )}

          <FAQSection />

          <div className='agd-tile agd-tile--cta-bottom'>
            <button
              className='agd-cta-bottom'
              onClick={handleRegister}
              style={noBlinkStyle}
            >
              <i className='ti ti-user-plus' /> Register for Tryout{' '}
              <i className='ti ti-arrow-right' />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Fallback ─────────────────────────────────────────────────────────

  return (
    <div
      className={`agd-root ${isLightTheme ? 'agd-root--light' : 'agd-root--dark'}`}
    >
      <div className='agd-event'>
        <div
          className='agd-tile agd-tile--hdr'
          style={{ borderTop: `3px solid #594230` }}
        >
          <div
            className='agd-hdr-icon'
            style={{
              color: '#594230',
              background: `#59423018`,
              borderColor: `#59423044`,
            }}
          >
            <i className='ti ti-ball-basketball' />
          </div>
          <h2 className='agd-title'>
            {config?.season || 'Registration'} {config?.year || ''}
          </h2>
        </div>
        {hasValidDescription(config?.description) && (
          <div className='agd-tile'>
            <div
              dangerouslySetInnerHTML={{ __html: config.description! }}
              style={{ lineHeight: 1.6 }}
            />
          </div>
        )}
        <div className='agd-tile agd-tile--cta'>
          <button
            className='agd-cta'
            style={{
              background: '#594230',
              boxShadow: `0 6px 20px #59423044`,
              ...noBlinkStyle,
            }}
            onClick={() => onRegister?.()}
          >
            <i className='ti ti-user-plus' /> Register Now{' '}
            <i className='ti ti-arrow-right' />
          </button>
        </div>
        <FAQSection />
        <div className='agd-tile agd-tile--cta-bottom'>
          <button
            className='agd-cta-bottom'
            onClick={() => onRegister?.()}
            style={noBlinkStyle}
          >
            <i className='ti ti-user-plus' /> Register Now{' '}
            <i className='ti ti-arrow-right' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoGridFromDescription;
